/**
 * ThemeVars.js — POINT UNIQUE de bascule Light <-> Dark (surcouche CSS).
 *
 * Le dark mode n'est PAS un second jeu de composants : c'est une surcouche CSS sur le
 * code couleur light existant. Les tokens neutres de theme.js sont des CSS custom properties
 * `var(--ev-*)`. Ici, et ICI SEULEMENT, on declare leurs valeurs :
 *   - :root              -> palette LIGHT (defaut, identique a l'actuel)
 *   - [data-theme=dark]  -> palette DARK (Noir / Blanc / Violet)
 *
 * Tout composant qui consomme les tokens theme.js (regle §20) bascule automatiquement,
 * sans correction. Un seul point de developpement pour les 2 modes.
 *
 * Bascule : `document.documentElement.setAttribute('data-theme','dark')` (provider racine + toggle).
 * Les couleurs de MARQUE (vert CTA, rouge danger, or) restent litterales dans theme.js ;
 * exception validee : le bouton CTA passe de vert (light) a violet (dark) via `--ev-cta`.
 *
 * GOTCHA HeaderDAV2.styles.js : `${COLORS}` n'y est PAS interprete -> on pilote ce fichier
 * par `var(--ev-*)`, jamais par swap de tokens. Le bloc legacy ci-dessous neutralise au point
 * unique les couleurs en dur restantes du header/footer Bootstrap (migration tokens = lot suivant).
 */
import { createGlobalStyle } from 'styled-components';

export const ThemeVars = createGlobalStyle`
  :root {
    /* Palette LIGHT (strictement l'actuel) */
    --ev-bg:           #f4f1f6;
    --ev-bg-subtle:    #fbfafc;
    --ev-surface:      #ffffff;
    --ev-ink:          #0d0a10;
    --ev-ink-soft:     rgba(13, 10, 16, 0.78);
    --ev-muted:        rgba(13, 10, 16, 0.55);
    --ev-ink-line:     rgba(13, 10, 16, 0.2);
    --ev-subtle:       rgba(13, 10, 16, 0.08);
    --ev-ink-faint:    rgba(13, 10, 16, 0.03);
    --ev-line:         #ececec;
    --ev-line-strong:  #d0d0d0;
    --ev-disabled-bg:  #cccccc;
    --ev-disabled-ink: #888888;
    /* CTA : vert en light */
    --ev-cta:          #1FCB5C;
    --ev-cta-hover:    #18B050;
    /* alertes feedback */
    --ev-danger-bg:    #fff0f0;
    --ev-warning-bg:   #fffbe6;
    /* Badges de statut feature requests — dark-aware */
    --ev-badge-neutral-bg:  #f4f1f6;
    --ev-badge-green-bg:    #f0fdf4;
    --ev-badge-magenta-bg:  #fdf0fb;
    --ev-badge-gold-bg:     #fffbf0;
    --ev-badge-selected-bg: #fdf0fb;
    /* fleche de <select> (data-URI) : stroke sombre en light */
    --ev-select-arrow: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%230d0a10' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
    /* poids typo : light = actuel */
    --ev-weight-title:  800;
    --ev-weight-filter: 600;
    /* ombre portee (toasts, cards flottantes) — dark-aware */
    --ev-shadow:        rgba(13, 10, 16, 0.1);
  }

  [data-theme="dark"] {
    /* Palette DARK : Noir / Blanc / Violet (validee Jonathan)
       2026-06-23 : ink-soft + muted en blanc plein (#f8f8f8) — Jonathan veut
       tous les textes gris en blanc. La hierarchie passe par weight/size/
       position, plus par opacite. */
    /* color-scheme: dark = signal au browser que TOUS les contrôles natifs
       (select option list, datepicker, scrollbar, autofill, file picker)
       doivent être rendus en variant dark. Sans ça, la liste déroulante d'un
       <select> reste en theme light OS — gris clair même si le <select>
       fermé a un bg dark. */
    color-scheme: dark;
    --ev-bg:           #000000;
    --ev-bg-subtle:    #070707;
    --ev-surface:      #0d0d0d;
    --ev-ink:          #f8f8f8;
    --ev-ink-soft:     #f8f8f8;
    --ev-muted:        #f8f8f8;
    --ev-ink-line:     rgba(255, 255, 255, 0.30);
    --ev-subtle:       rgba(255, 255, 255, 0.07);
    --ev-ink-faint:    rgba(255, 255, 255, 0.04);
    --ev-line:         rgba(255, 255, 255, 0.20);
    --ev-line-strong:  rgba(255, 255, 255, 0.34);
    --ev-disabled-bg:  #1e1e1e;
    --ev-disabled-ink: #5a5a5a;
    /* CTA : boutons verts -> violet en dark */
    --ev-cta:          #C218A6;
    --ev-cta-hover:    #A6148E;
    /* alertes feedback */
    --ev-danger-bg:    #2a1515;
    --ev-warning-bg:   #2a2415;
    /* fleche de <select> : stroke blanc en dark */
    --ev-select-arrow: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23f8f8f8' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
    /* poids typo : titres + filtres plus gras en dark */
    --ev-weight-title:  900;
    --ev-weight-filter: 700;
    /* ombre portee : noire plus dense en dark (l'ombre claire est invisible) */
    --ev-shadow:        rgba(0, 0, 0, 0.6);
    /* TileMap ocean bg */
    --ev-sea:           #050510;
    /* Badges de statut feature requests — dark-aware */
    --ev-badge-neutral-bg:  rgba(255, 255, 255, 0.08);
    --ev-badge-green-bg:    rgba(31, 203, 92, 0.12);
    --ev-badge-magenta-bg:  rgba(194, 24, 166, 0.12);
    --ev-badge-gold-bg:     rgba(232, 172, 58, 0.12);
    --ev-badge-selected-bg: rgba(194, 24, 166, 0.10);
  }

  /* Surcouche legacy (couleurs en dur non encore tokenisees) - DARK only.
     Header Bootstrap #myHeader + footer-light. Migration vers tokens = lot suivant. */
  [data-theme="dark"] body { background: #000000; }

  [data-theme="dark"] header#myHeader.navbar,
  [data-theme="dark"] header#myHeader.navbar.sticky,
  [data-theme="dark"] header#myHeader.navbar.solidnav {
    background: #000000 !important;
    box-shadow: 0 1px 0 #000000 !important;
    border-bottom: none !important;
  }
  [data-theme="dark"] header#myHeader.navbar .btn,
  [data-theme="dark"] header#myHeader.navbar a,
  [data-theme="dark"] header#myHeader.navbar .navbar-item,
  [data-theme="dark"] header#myHeader.navbar.sticky .btn,
  [data-theme="dark"] header#myHeader.navbar.sticky a,
  [data-theme="dark"] header#myHeader.navbar.solidnav .btn,
  [data-theme="dark"] header#myHeader.navbar.solidnav a,
  [data-theme="dark"] header#myHeader.navbar.solidnav .navbar-item {
    color: #f8f8f8 !important;
  }
  [data-theme="dark"] header#myHeader.navbar.sticky .dropdown-toggle::after,
  [data-theme="dark"] header#myHeader.navbar.solidnav .dropdown-toggle::after {
    color: #f8f8f8 !important;
  }
  [data-theme="dark"] .item-dropdown {
    background: #0d0d0d !important;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.8) !important;
  }
  [data-theme="dark"] .item-dropdown .dropdown a {
    color: #f8f8f8 !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
  }
  [data-theme="dark"] .item-dropdown .dropdown a:hover {
    background: #C218A6 !important;
    color: #fff !important;
  }
  [data-theme="dark"] .navbar .menu-line,
  [data-theme="dark"] .navbar .menu-line1,
  [data-theme="dark"] .navbar .menu-line2 { background: #f8f8f8 !important; }
  @media only screen and (max-width: 1199px) {
    [data-theme="dark"] .navbar { background: #000000 !important; }
  }

  [data-theme="dark"] footer.footer-light {
    background: #000000 !important;
    color: #f8f8f8 !important;
  }
  [data-theme="dark"] footer.footer-light a { color: #f8f8f8 !important; }
  [data-theme="dark"] footer.footer-light .subfooter { border-top: 1px solid rgba(255, 255, 255, 0.1) !important; }

  /* =========================================================================
     HEADER MENU — surcouche dark V1 (style "sidebar Settings" appliqué au menu)
     Toutes les regles ci-dessous ciblent [data-theme="dark"] ; le light est inchange.
     Valeurs litterales CSS (pas d'interpolation JS de tokens) — respecte le GOTCHA HeaderDAV2 note en tete.
     SPEC : surface #0d0d0d · texte #f8f8f8 · hover rgba(194,24,166,.14)+#C218A6 · actif #C218A6 ·
            separateur rgba(255,255,255,.12).
     ========================================================================= */

  /* 1. Separateur "bullet" entre items de menu nav → invisible en dark
        (HeaderDAV2 l.63-72 : .navbar-item:not(:last-child)::after couleur rgba(13,10,16,.3)) */
  [data-theme="dark"] header#myHeader .menu .navbar-item:not(:last-child)::after {
    color: transparent !important;
  }

  /* 2. Texte des liens de menu (labels .dropdown-custom.btn) → blanc en dark
        Specificite haute (.navbar.solidnav) requise pour battre HeaderDAV2 qui force #1a1430. */
  [data-theme="dark"] header#myHeader.navbar.solidnav .menu .navbar-item .dropdown-custom.btn,
  [data-theme="dark"] header#myHeader.navbar.solidnav .menu .dropdown-custom.btn,
  [data-theme="dark"] header#myHeader .menu .navbar-item .dropdown-custom.btn {
    color: #f8f8f8 !important;
  }
  [data-theme="dark"] header#myHeader.navbar.solidnav .menu .navbar-item:hover .dropdown-custom.btn,
  [data-theme="dark"] header#myHeader .menu .navbar-item:hover .dropdown-custom.btn {
    color: #C218A6 !important;
  }
  [data-theme="dark"] header#myHeader.navbar.solidnav .menu .navbar-item a.active .dropdown-custom.btn,
  [data-theme="dark"] header#myHeader .menu .navbar-item a.active .dropdown-custom.btn {
    color: #C218A6 !important;
  }

  /* 3. Boutons icones droite (.h-icon-btn) : contour fondu + couleur icone blanc */
  [data-theme="dark"] header#myHeader .mainside .h-icon-btn {
    border-color: rgba(255, 255, 255, 0.10) !important;
    color: #f8f8f8 !important;
  }
  [data-theme="dark"] header#myHeader .mainside .h-icon-btn:hover {
    background: rgba(194, 24, 166, 0.14) !important;
    border-color: rgba(194, 24, 166, 0.35) !important;
    color: #C218A6 !important;
  }

  /* Separateur vertical entre groupe icones et CTA/avatar */
  [data-theme="dark"] header#myHeader .mainside .h-sep {
    background: rgba(255, 255, 255, 0.10) !important;
  }

  /* 4. Cloche (.de-menu-notification) : contour + fond fondus */
  [data-theme="dark"] header#myHeader .de-menu-notification {
    border-color: rgba(255, 255, 255, 0.10) !important;
    background: transparent !important;
    color: #f8f8f8 !important;
  }

  /* 5. Toggle theme (.theme-toggle) : contour fondu en dark OFF (en ON = deja violet via .is-dark) */
  [data-theme="dark"] header#myHeader .mainside .theme-toggle {
    border-color: rgba(255, 255, 255, 0.12) !important;
  }
  /* OFF → fond tres leger pour distinguer du background noir */
  [data-theme="dark"] header#myHeader .mainside .theme-toggle:not(.is-dark) {
    background: rgba(255, 255, 255, 0.06) !important;
  }

  /* 6. Sous-menu "Maps" (navigation-content) : fond blanc → #0d0d0d, separateurs → rgba blanc */
  [data-theme="dark"] header#myHeader .menu .navigation-content {
    background: #0d0d0d !important;
    border-color: rgba(255, 255, 255, 0.12) !important;
    box-shadow: 0 1.2rem 3rem rgba(0, 0, 0, 0.9) !important;
  }
  /* fleche caret du sous-menu Maps */
  [data-theme="dark"] header#myHeader .menu .navigation-content::before {
    background: #0d0d0d !important;
    border-left-color: rgba(255, 255, 255, 0.12) !important;
    border-top-color: rgba(255, 255, 255, 0.12) !important;
  }
  /* items du sous-menu Maps — specificite haute (.navbar.solidnav) requise pour
     battre le hover parent (.navbar-item:hover .dropdown-custom.btn) qui cascade. */
  [data-theme="dark"] header#myHeader.navbar.solidnav .menu .navigation-content a.landSubmenu .dropdown-custom.btn,
  [data-theme="dark"] header#myHeader .menu .navigation-content a.landSubmenu .dropdown-custom.btn {
    color: #f8f8f8 !important;
  }
  [data-theme="dark"] header#myHeader.navbar.solidnav .menu .navigation-content a.landSubmenu:hover .dropdown-custom.btn,
  [data-theme="dark"] header#myHeader .menu .navigation-content a.landSubmenu:hover .dropdown-custom.btn {
    background: rgba(194, 24, 166, 0.14) !important;
    color: #C218A6 !important;
  }

  /* 7. Dropdown profil (.de-menu-profile .popshow) : fond blanc → #0d0d0d */
  [data-theme="dark"] header#myHeader .de-menu-profile .popshow {
    background: #0d0d0d !important;
    border-color: rgba(255, 255, 255, 0.12) !important;
    box-shadow: 0 1.2rem 3rem rgba(0, 0, 0, 0.9) !important;
  }
  /* caret/fleche du popshow profil */
  [data-theme="dark"] header#myHeader .de-menu-profile .popshow::before {
    background: #0d0d0d !important;
    border-left-color: rgba(255, 255, 255, 0.12) !important;
    border-top-color: rgba(255, 255, 255, 0.12) !important;
  }
  /* liseré violet en tete du popshow : conserve (#C218A6) — pas de surcharge */

  /* 7a. En-tete dd-head : texte nom + adresse */
  [data-theme="dark"] header#myHeader .de-menu-profile .popshow .dd-name {
    color: #f8f8f8 !important;
  }
  [data-theme="dark"] header#myHeader .de-menu-profile .popshow .dd-addr {
    color: #f8f8f8 !important;
  }

  /* 7b. Liseré degradé interne */
  [data-theme="dark"] header#myHeader .de-menu-profile .popshow .d-line {
    background: linear-gradient(90deg, rgba(255,255,255,.12), transparent) !important;
  }

  /* 7b-bis. Lien "Get earnicoins" : magenta clair inversé sur fond sombre */
  [data-theme="dark"] header#myHeader .de-menu-profile .popshow .dd-earnicoins .dd-earnicoins-get {
    color: #ff5fd6 !important;
  }
  [data-theme="dark"] header#myHeader .de-menu-profile .popshow .dd-earnicoins .dd-earnicoins-get:hover {
    color: #ff8ae2 !important;
  }

  /* 7c. Items de sous-menu (.de-submenu-profile) : texte blanc, icones blanches */
  [data-theme="dark"] header#myHeader .de-menu-profile .popshow .de-submenu-profile li span {
    color: #f8f8f8 !important;
  }
  /* hover : fond violet soft + texte/icone violet */
  [data-theme="dark"] header#myHeader .de-menu-profile .popshow .de-submenu-profile li span:hover {
    background: rgba(194, 24, 166, 0.14) !important;
    color: #C218A6 !important;
  }
  [data-theme="dark"] header#myHeader .de-menu-profile .popshow .de-submenu-profile li span:hover i.fa {
    color: #C218A6 !important;
    background: rgba(194, 24, 166, 0.20) !important;
  }
  /* icone pastille : fond tres leger + icone blanche par defaut (violet au hover ci-dessus) */
  [data-theme="dark"] header#myHeader .de-menu-profile .popshow .de-submenu-profile li span i.fa {
    background: rgba(255, 255, 255, 0.08) !important;
    color: #f8f8f8 !important;
  }
  /* Sign out : reste rouge (pas de surcharge couleur) */
  /* item actif/selectionne (si classe .active ou .selected) : fond violet plein + texte blanc */
  [data-theme="dark"] header#myHeader .de-menu-profile .popshow .de-submenu-profile li.active span,
  [data-theme="dark"] header#myHeader .de-menu-profile .popshow .de-submenu-profile li.selected span {
    background: #C218A6 !important;
    color: #ffffff !important;
  }
  [data-theme="dark"] header#myHeader .de-menu-profile .popshow .de-submenu-profile li.active span i.fa,
  [data-theme="dark"] header#myHeader .de-menu-profile .popshow .de-submenu-profile li.selected span i.fa {
    background: rgba(255, 255, 255, 0.20) !important;
    color: #ffffff !important;
  }

  /* 8. Widget notifications (.de-menu-notification .popshow.notif-pop) */
  [data-theme="dark"] header#myHeader .de-menu-notification .popshow.notif-pop {
    background: #0d0d0d !important;
    border-color: rgba(255, 255, 255, 0.12) !important;
    box-shadow: 0 1.2rem 3rem rgba(0, 0, 0, 0.9) !important;
  }
  /* caret */
  [data-theme="dark"] header#myHeader .de-menu-notification .popshow.notif-pop::before {
    background: #0d0d0d !important;
    border-left-color: rgba(255, 255, 255, 0.12) !important;
    border-top-color: rgba(255, 255, 255, 0.12) !important;
  }
  /* en-tete nhead */
  [data-theme="dark"] header#myHeader .de-menu-notification .popshow.notif-pop .nhead {
    border-bottom-color: rgba(255, 255, 255, 0.12) !important;
  }
  [data-theme="dark"] header#myHeader .de-menu-notification .popshow.notif-pop .nhead h3 {
    color: #f8f8f8 !important;
  }
  /* separateur entre items */
  [data-theme="dark"] header#myHeader .de-menu-notification .popshow.notif-pop li + li {
    border-top-color: rgba(255, 255, 255, 0.07) !important;
  }
  /* texte des notifications */
  [data-theme="dark"] header#myHeader .de-menu-notification .popshow.notif-pop .ntxt p {
    color: #f8f8f8 !important;
  }
  [data-theme="dark"] header#myHeader .de-menu-notification .popshow.notif-pop .ntxt time {
    color: #f8f8f8 !important;
  }
  /* footer notif */
  [data-theme="dark"] header#myHeader .de-menu-notification .popshow.notif-pop .nfoot {
    background: #0d0d0d !important;
    border-top-color: rgba(255, 255, 255, 0.12) !important;
  }

  /* 9. Menu mobile (.menu.menu-open) : fond blanc → #0d0d0d, liens → blancs */
  [data-theme="dark"] header#myHeader .menu.menu-open {
    background: #0d0d0d !important;
    border-top-color: #C218A6 !important;
  }
  [data-theme="dark"] header#myHeader .menu.menu-open .navbar-item {
    border-bottom-color: rgba(255, 255, 255, 0.08) !important;
  }
  [data-theme="dark"] header#myHeader .menu.menu-open a,
  [data-theme="dark"] header#myHeader .menu.menu-open a:visited,
  [data-theme="dark"] header#myHeader .menu.menu-open a:hover,
  [data-theme="dark"] header#myHeader .menu.menu-open a.active,
  [data-theme="dark"] header#myHeader .menu.menu-open a.non-active {
    color: #f8f8f8 !important;
  }
  [data-theme="dark"] header#myHeader .menu.menu-open .dropdown-custom.btn {
    color: #f8f8f8 !important;
  }
  [data-theme="dark"] header#myHeader .menu.menu-open .navigation-content a {
    color: #f8f8f8 !important;
  }
  [data-theme="dark"] header#myHeader .menu.menu-open .navigation-content a:hover {
    color: #C218A6 !important;
  }

  /* 10. Logo mobile : icone EV noire (brightness(0)) → blanche en dark */
  [data-theme="dark"] header#myHeader .logo .logo-mobile-icon {
    filter: brightness(0) invert(1) !important;
  }

  /* 11. Hamburger barres : sombres (#1a1430) → blanches en dark */
  [data-theme="dark"] header#myHeader button.nav-icon .menu-line,
  [data-theme="dark"] header#myHeader button.nav-icon .menu-line1,
  [data-theme="dark"] header#myHeader button.nav-icon .menu-line2 {
    background: #f8f8f8 !important;
  }

  /* 12. Modal dark — ModalDA primitives (card via data-ev="modal-card") */
  [data-theme="dark"] [data-ev="modal-card"] {
    background: #000000 !important;
    border: 2px solid rgba(194, 24, 166, 0.35) !important;
    box-shadow: 0 0 2rem rgba(194, 24, 166, 0.12), 0 1rem 3rem rgba(0, 0, 0, 0.7) !important;
  }
  /* 12b. Croix de fermeture modal : UN SEUL liseret noir (invisible sur header magenta) */
  [data-theme="dark"] [data-ev="modal-card"] button[aria-label="Close"] {
    border: 0.0625rem solid rgba(0, 0, 0, 0.5) !important;
    background: rgba(255, 255, 255, 0.08) !important;
    outline: none !important;
    box-shadow: none !important;
  }
  [data-theme="dark"] [data-ev="modal-card"] button[aria-label="Close"]:hover {
    background: rgba(255, 255, 255, 0.18) !important;
  }
  [data-theme="dark"] [data-ev="modal-card"] button[aria-label="Close"]:focus,
  [data-theme="dark"] [data-ev="modal-card"] button[aria-label="Close"]:focus-visible {
    outline: none !important;
    box-shadow: none !important;
  }

  /* 12a. TileMap Legend dark */
  [data-theme="dark"] .tile-map-legend {
    background: #0d0d0d !important;
    border: 1px solid rgba(194, 24, 166, 0.15) !important;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.6) !important;
    color: #f8f8f8 !important;
  }
  [data-theme="dark"] .tile-map-legend .tile-map-legend-label {
    color: #f8f8f8 !important;
  }
  [data-theme="dark"] .tile-map-legend .fw-bold {
    color: #f8f8f8 !important;
  }

  /* 14. Pages Author/Profile legacy (classes SCSS non préfixées .greyscheme) :
        la page /Author/<id> n'applique pas .greyscheme sur <body> donc les overrides
        legacy color:#fff ne s'activent pas → titre & avatars invisibles en dark.
        Fix chirurgical (override dark uniquement, light inchangé). */
  [data-theme="dark"] .profile_avatar img,
  [data-theme="dark"] .author_list_pp img,
  [data-theme="dark"] .d_coll .d_profile_img img {
    background-color: #ffffff !important;
  }
  [data-theme="dark"] .profile_name h4,
  [data-theme="dark"] .profile_name .profile_username,
  [data-theme="dark"] .profile_name .profile_wallet {
    color: #f8f8f8 !important;
  }

  /* 15. TileMap loading placeholder (img statique map.webp pendant le chargement
        des canvas Land/TileMapV2) : palette claire (sable + bleu océan) qui jure
        sur fond noir en dark. Filtre noir-et-blanc inversé (≈ négatif) en attente. */
  [data-theme="dark"] img.tile-map-static {
    filter: invert(1) grayscale(1) brightness(0.95) contrast(0.95);
  }

  /* 13. Global form elements catch-all (textarea, .form-control, native inputs).
     bg = var(--ev-bg) (fond de page, #000) — un select isolé hors carte (ex.
     Notifications filtre période) doit se fondre dans la page, pas former un
     îlot gris (var(--ev-surface) = #0d0d0d ressortait gris sur #000). La bordure
     var(--ev-line) garde la délimitation visible. */
  [data-theme="dark"] textarea,
  [data-theme="dark"] .form-control,
  [data-theme="dark"] select:not([class*="Styled"]):not([class*="styled"]) {
    background-color: var(--ev-bg, #000) !important;
    color: var(--ev-ink, #f8f8f8) !important;
    border-color: var(--ev-line, rgba(255, 255, 255, 0.12)) !important;
  }
  [data-theme="dark"] textarea::placeholder,
  [data-theme="dark"] .form-control::placeholder {
    color: #f8f8f8 !important;
  }
`;

export default ThemeVars;
